import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TDR } from 'tdr-common';
import { HASH_ELEMENT_IDS } from '../../common/hashElementIds';
import { FILTER_CHANGED, analytics } from '../../helpers/analytics';
import { useFilterAnalytics } from '../../helpers/analytics/filterContext';
import { SearchFilters } from '../../routes/AvailabilityGuard';
import { GroupSizeButtons } from '../shared/GroupSizeButtons';
import inRange from 'lodash/inRange';
import { Typography } from './Typography';
import DownArrow from '../../icons/down-chevron.svg';

export interface PartySizeOption {
	value: string;
	label: string;
	disabled: boolean;
	inboundRequest: boolean;
}
type PartySizeSelectorProps = {
	searchFilters: SearchFilters;
	onChange?: (newOption: PartySizeOption) => void;
	defaultValue?: number;
	options: PartySizeOption[];
	analyticsOption?: any;
};

export function PartySizeSelector({
	searchFilters,
	options = [],
	onChange,
	analyticsOption = {}
}: PartySizeSelectorProps) {
	const { t } = useTranslation();
	const filterCtx = useFilterAnalytics('global page');
	const [selectedOption, setSelectedOption] = useState<PartySizeOption | undefined>();
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);

	useEffect(() => {
		if (searchFilters?.groupSize) {
			setSelectedOption(options.find((option) => Number(option.value) === searchFilters.groupSize));
		}
		else {
			setSelectedOption(undefined);
		}
	}, [searchFilters.groupSize, options]);

	// Add effect to check scroll state when options change
	useEffect(() => {
		// Use setTimeout to ensure the DOM has updated with the new options
		const timer = setTimeout(() => {
			if (scrollContainer) {
				const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
				setCanScrollLeft(scrollLeft > 0);
				setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
			}
		}, 0);

		return () => clearTimeout(timer);
	}, [options, scrollContainer]);

	useEffect(() => {
		const checkScroll = () => {
			if (scrollContainer) {
				const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
				setCanScrollLeft(scrollLeft > 0);
				setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
			}
		};

		if (scrollContainer) {
			checkScroll();
			scrollContainer.addEventListener('scroll', checkScroll);
			window.addEventListener('resize', checkScroll);
		}

		return () => {
			if (scrollContainer) {
				scrollContainer.removeEventListener('scroll', checkScroll);
				window.removeEventListener('resize', checkScroll);
			}
		};
	}, [scrollContainer]);

	const handleButtonChange = (option: PartySizeOption) => {
		setSelectedOption(option);
		onChange?.(option);
		analytics.track(FILTER_CHANGED, {
			filterName: 'groupSize',
			filterValue: option.value,
			...analyticsOption,
			...filterCtx
		});
	};

	const handleScroll = (direction: 'left' | 'right') => {
		if (scrollContainer) {
			const scrollAmount = 186;
			const newScrollLeft = direction === 'left'
				? scrollContainer.scrollLeft - scrollAmount
				: scrollContainer.scrollLeft + scrollAmount;
			scrollContainer.scrollTo({
				left: newScrollLeft,
				behavior: 'smooth'
			});
		}
	};

	return (
		<div className="my-2 md:my-3 lg:my-4" id={HASH_ELEMENT_IDS.groupSize}>
			<div className="h-[64px] flex items-center justify-between min-[440px]:max-[639px]:mt-10">
				<Typography variant="label" color="#D4D4D8">
					{t('labels.group_size', { defaultValue: 'Group Size' })}
				</Typography>
				<div className="flex items-center gap-2">
					<button
						onClick={() => handleScroll('left')}
						disabled={!canScrollLeft}
						className={`size-[40px] flex items-center justify-center transition-all ${
							canScrollLeft ? 'opacity-100 cursor-pointer' : 'opacity-40 cursor-not-allowed'
						}`}
						aria-label="Scroll left"
					>
						<img src={DownArrow} alt="Previous" className="rotate-90 size-[44px]" />
					</button>
					<button
						onClick={() => handleScroll('right')}
						disabled={!canScrollRight}
						className={`size-[40px] flex items-center justify-center transition-all ${
							canScrollRight ? 'opacity-100 cursor-pointer' : 'opacity-40 cursor-not-allowed'
						}`}
						aria-label="Scroll right"
					>
						<img src={DownArrow} alt="Next" className="-rotate-90 size-[44px]" />
					</button>
				</div>
			</div>
			<div ref={(el) => setScrollContainer(el?.querySelector('.flex.overflow-x-auto') as HTMLDivElement)}>
				<GroupSizeButtons
					options={options}
					selected={selectedOption ? Number(selectedOption.value) : undefined}
					onChange={handleButtonChange}
				/>
			</div>
		</div>
	);
}

export const buildGroupSizeOptions = (
	guestNumbers: number[],
	selectedTable: TDR.Table,
	showInboundOption?: boolean,
	t?: (key: string, options?: any) => string
): [PartySizeOption[], PartySizeOption | null] => {
	const guestOptions = guestNumbers
		?.map((num, idx) => {
			if (!!num) {
				return {
					value: idx.toString(),
					label: t ? `${idx} ${idx > 1 ? t('labels.guests', { defaultValue: 'Guests' }) : t('labels.guest', { defaultValue: 'Guest' })}` : `${idx} Guest${idx > 1 ? 's' : ''}`,
					disabled: !!(
						selectedTable && !inRange(idx, selectedTable.minGuests, selectedTable.maxGuests + 1)
					),
					inboundRequest: false
				};
			}
			return null;
		})
		.filter((val) => !!val);

	let leadGenOption: PartySizeOption | null = null;

	if (showInboundOption && guestOptions?.length > 0) {
		const num = guestOptions[guestOptions.length - 1].value;
		const numInt = parseInt(num) + 1;
		leadGenOption = {
			value: numInt.toString(),
			label: t ? `${numInt}+ ${t('labels.guests', { defaultValue: 'Guests' })}` : `${numInt}+ Guest${numInt > 1 ? 's' : ''}`,
			disabled: false,
			inboundRequest: true
		};
		guestOptions.push(leadGenOption);
	}

	return [guestOptions, leadGenOption];
};